import React from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { SOLITUDE } from '@espacePro/constants/admin-theme';
import {
  FONT_FAMILY_MAIN, LIGHT_PALE_GREY, PALE_GREY, TEXT_COLOR,
} from '../../../main-app/theme';

const Container = styled.div`
  display: inline-flex;
  border-radius: 9px;
  ${(props) => props.isInSeparateMode
    ? css`
          flex-wrap: wrap;
        `
    : css`
          background: #fff;
          box-shadow: 0 0 0 1px #f4f7f9;
          flex-wrap: nowrap;
        `},
`;

const Label = styled.label`
  padding: 0 12px;
  font-family: ${(props) => props?.theme?.fontFamily || FONT_FAMILY_MAIN};
  font-size: ${(props) => (props.fontSize ? props.fontSize : 16)}px;
  color: ${(props) => props?.theme?.colors?.textColorMain || TEXT_COLOR};
  cursor: pointer;
  height: 37px;
  line-height: 37px;
  margin: 0;
  font-weight: normal;
  display: inline-block;
  width: 100%;
  min-width: 33px;
  max-width: 100%;
  text-align: center;
  white-space: nowrap;

  &:hover,
  &:focus {
    background-color: ${LIGHT_PALE_GREY};
  }

  svg {
    vertical-align: middle;
    margin-left: 8px;
  }
`;

const SwitcherItem = styled.div`
  flex: 0;

  & + & {
    border-left: 1px solid ${SOLITUDE};
  }

  &:first-child {
    ${Label} {
      border-radius: 9px 0 0 9px;
    }
  }

  &:last-child {
    ${Label} {
      border-radius: 0 9px 9px 0;
    }
  }
`;

const SeparateSwitcherItem = styled.div`
  margin: 0 8px 8px 0;
  box-shadow: 0 1px 1px 0 rgba(0, 0, 0, 0.05);
  background-color: #ffffff;
  border-radius: 8px;

  ${Label} {
    border-radius: 8px;
  }
`;

const Input = styled.input`
  position: absolute !important;
  height: 1px;
  width: 1px;
  overflow: hidden;
  clip: rect(1px, 1px, 1px, 1px);
  white-space: nowrap;

  &:focus + ${Label} {
    background-color: ${LIGHT_PALE_GREY};
  }

  &:checked + ${Label} {
    background-color: ${({ theme }) => theme.colors?.switcherBackground ? theme.colors?.switcherBackground : PALE_GREY};
    box-shadow: inset 0 1px 1px 0 rgba(0, 0, 0, 0.05);
    color: ${({ theme }) => theme.colors?.switcherColor ? theme.colors?.switcherColor : '#979ea7'};
  }

  &:disabled + ${Label} {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

type Item = string | boolean | number | { label: string; value: string | boolean | number };

type SwitcherTypes = {
  items: Item[];
  onChange?: () => void;
  onClick?: () => void;
  name: string;
  activeItem?: boolean | number | string | any[];
  activeItemIcon?: React.ReactNode;
  fontSize?: number;
  isMultipleChoiceAllowed?: boolean;
  isInSeparateMode?: boolean;
  disabled?: boolean;
};

const Switcher = (props: SwitcherTypes) => {
  const {
    items,
    name,
    onChange,
    activeItem,
    fontSize,
    isMultipleChoiceAllowed,
    isInSeparateMode,
    onClick,
    activeItemIcon,
    disabled,
  } = props;

  const SwitcherItemElem = isInSeparateMode ? SeparateSwitcherItem : SwitcherItem;
  return (
    <Container isInSeparateMode={isInSeparateMode}>
      {items.map((item, i) => {
        let itemValue;
        let isActive;
        if (typeof item === 'object' && item !== null) {
          itemValue = item.value;
        } else {
          itemValue = item;
        }
        if (Array.isArray(activeItem)) {
          isActive = activeItem.indexOf(itemValue) > -1;
        } else {
          isActive = activeItem === itemValue;
        }

        return (
          <SwitcherItemElem key={itemValue}>
            <Input
              type={isMultipleChoiceAllowed ? 'checkbox' : 'radio'}
              name={name}
              id={`${name}_input-${i}`}
              value={itemValue}
              checked={isActive}
              onChange={onChange}
              onClick={onClick}
              data-test-selector={`form-field-${name}`}
              disabled={disabled}
            />
            <Label htmlFor={`${name}_input-${i}`} fontSize={fontSize}>
              {typeof item === 'object' && item !== null && item.label ? item.label : item}
              {isActive && activeItemIcon ? activeItemIcon : null}
            </Label>
          </SwitcherItemElem>
        );
      })}
    </Container>
  );
};

Switcher.propTypes = {
  items: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.number])),
    PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.number]),
      }),
    ),
  ]).isRequired,
  onChange: PropTypes.func,
  onClick: PropTypes.func,
  name: PropTypes.string.isRequired,
  activeItem: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.number,
    PropTypes.string,
    PropTypes.array,
  ]),
  activeItemIcon: PropTypes.node,
  fontSize: PropTypes.number,
  isMultipleChoiceAllowed: PropTypes.bool,
  isInSeparateMode: PropTypes.bool,
  disabled: PropTypes.bool,
};

Switcher.defaultProps = {
  onChange: () => {},
  onClick: () => {},
  activeItem: '',
  fontSize: 16,
  isMultipleChoiceAllowed: false,
  isInSeparateMode: false,
  activeItemIcon: undefined,
  disabled: false,
};

export { Switcher };
